import {
  PageHeader,
  StatTile,
  StatusBadge,
  setupChangesPath,
  VesselParticularsDrawer,
} from '@felix/ui';
import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Menu,
  Skeleton,
  Table,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconAnchor,
  IconCircleCheck,
  IconDots,
  IconFileDescription,
  IconHourglassHigh,
  IconKey,
  IconListDetails,
  IconLock,
  IconPlus,
  IconRefresh,
  IconRocket,
  IconShip,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ApiError, type FleetList, type FleetVessel, officeApi, type Vessel } from '../api.js';
import { AddVesselModal } from '../components/AddVesselModal.js';
import { GoLiveModal } from '../components/GoLiveModal.js';
import { SetupCodeModal } from '../components/SetupCodeModal.js';
import { Shell } from '../components/Shell.js';
import {
  addVesselControl,
  type FLEET_PERMISSIONS,
  fleetDeniedText,
  particularsReadOnly,
  setupStateOf,
  summarizeFleet,
  vesselControls,
} from '../fleet.js';
import classes from './FleetPage.module.css';

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; fleet: FleetList };

/**
 * Filo: gemiler ve gemi sunucularının durumu (mimari belge §12 "filo panosu"). Ofis buradan gemi
 * ekler ve gemi sunucusu için kurulum kodu üretir (§5.2). Düğmeler sunucunun yetki bayraklarına
 * göredir; yetkisi olmayana düğme yerine kısa açıklama gösterilir (ADR 0151 §1).
 */
export function FleetPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [adding, setAdding] = useState(false);
  const [coding, setCoding] = useState<Vessel | null>(null);
  // "Kullanıma al" onayı (ADR 0142 §2)
  const [goingLive, setGoingLive] = useState<Vessel | null>(null);
  // Gemi özellikleri (M7a Task 6c): Equipment List üst kartının Vessel Type, Built, Flag değerleri
  const [particulars, setParticulars] = useState<string | null>(null);

  const load = useCallback(() => {
    setState({ kind: 'loading' });
    officeApi
      .fleet()
      .then((fleet) => setState({ kind: 'ready', fleet }))
      .catch((error: unknown) =>
        setState({ kind: 'error', code: error instanceof ApiError ? error.code : 'GENERIC' }),
      );
  }, []);
  useEffect(load, [load]);

  const vessels = state.kind === 'ready' ? state.fleet.vessels : [];
  const summary = useMemo(() => summarizeFleet(vessels), [vessels]);

  /** Düğme yerine açıklama: matristeki yetki, kapsamı ve varsayılanda hangi rolde (ADR 0151 §1). */
  const deniedText = (kind: keyof typeof FLEET_PERMISSIONS, text: string = kind) =>
    fleetDeniedText(t, kind, text);
  const denied = (kind: keyof typeof FLEET_PERMISSIONS) => (
    <Text component="span" fz="xs" c="dimmed" className={classes.denied}>
      <IconLock size={14} aria-hidden className={classes.deniedIcon} />
      <span>{deniedText(kind)}</span>
    </Text>
  );

  const status = (vessel: Vessel) =>
    vessel.activated ? (
      <StatusBadge tone="success">{t('fleet.activated')}</StatusBadge>
    ) : (
      <StatusBadge tone="warning">{t('fleet.notActivated')}</StatusBadge>
    );

  /** Kurulum aşaması rozeti (ADR 0142 §1): kurulumda ofis düzeltir, canlıda revizyon ve talep. */
  const stage = (vessel: Vessel) =>
    setupStateOf(vessel) === 'live' ? (
      <StatusBadge tone="success">{t('fleet.stage.live')}</StatusBadge>
    ) : (
      <StatusBadge tone="info">{t('fleet.stage.setup')}</StatusBadge>
    );

  /**
   * Etkin olmayan gemide birincil işlem kurulum kodudur; etkin gemide menüde, uyarıyla. Gemi
   * özellikleri her gemide menüde (M7a Task 6c). Yetkisi olmayana düğme yerine açıklama.
   */
  const actions = (vessel: FleetVessel) => {
    const controls = vesselControls(vessel);
    return (
      <span className={classes.rowActions}>
        {controls.goLive === 'button' ? (
          <Button
            variant="light"
            size="xs"
            leftSection={<IconRocket size={16} />}
            onClick={() => setGoingLive(vessel)}
          >
            {t('fleet.goLive.action')}
          </Button>
        ) : null}
        {controls.goLive === 'denied' ? denied('goLive') : null}
        {controls.setupCode === 'button' ? (
          <Button
            variant="light"
            size="xs"
            leftSection={<IconKey size={16} />}
            onClick={() => setCoding(vessel)}
          >
            {t('fleet.setupCode')}
          </Button>
        ) : null}
        {controls.setupCode === 'denied' ? denied('setupCode') : null}
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              aria-label={t('fleet.more', { vessel: vessel.name })}
            >
              <IconDots size={18} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconFileDescription size={16} />}
              onClick={() => setParticulars(vessel.id)}
            >
              {t('pms.particulars.title')}
            </Menu.Item>
            {/* Kurulum değişiklikleri (ADR 0142 §3): gemi sunucusu etkin gemide gönderilir */}
            {vessel.activated ? (
              <Menu.Item
                leftSection={<IconListDetails size={16} />}
                onClick={() => navigate(setupChangesPath(vessel.id))}
              >
                {t('pms.setupChangeList.title')}
              </Menu.Item>
            ) : null}
            {controls.replaceServer === 'hidden' ? null : (
              <Menu.Item
                leftSection={<IconRefresh size={16} />}
                disabled={controls.replaceServer === 'denied'}
                onClick={() => setCoding(vessel)}
              >
                {t('fleet.replaceServer')}
                {controls.replaceServer === 'denied' ? (
                  <Text component="span" fz="xs" c="dimmed" display="block">
                    {deniedText('replaceServer', 'short')}
                  </Text>
                ) : null}
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      </span>
    );
  };

  /** Dar ekran kartının düğmeleri: tablo satırıyla aynı kurallar (ADR 0151 §1). */
  const cardActions = (vessel: FleetVessel) => {
    const controls = vesselControls(vessel);
    return (
      <>
        {controls.goLive === 'button' ? (
          <Button
            variant="light"
            fullWidth
            leftSection={<IconRocket size={16} />}
            onClick={() => setGoingLive(vessel)}
          >
            {t('fleet.goLive.action')}
          </Button>
        ) : null}
        {controls.goLive === 'denied' ? denied('goLive') : null}
        <Button
          variant="default"
          fullWidth
          leftSection={<IconFileDescription size={16} />}
          onClick={() => setParticulars(vessel.id)}
        >
          {t('pms.particulars.title')}
        </Button>
        {controls.replaceServer === 'button' ? (
          <Button
            variant="default"
            fullWidth
            leftSection={<IconRefresh size={16} />}
            onClick={() => setCoding(vessel)}
          >
            {t('fleet.replaceServer')}
          </Button>
        ) : null}
        {controls.replaceServer === 'denied' ? denied('replaceServer') : null}
        {controls.setupCode === 'button' ? (
          <Button
            variant="light"
            fullWidth
            leftSection={<IconKey size={16} />}
            onClick={() => setCoding(vessel)}
          >
            {t('fleet.setupCode')}
          </Button>
        ) : null}
        {controls.setupCode === 'denied' ? denied('setupCode') : null}
      </>
    );
  };

  // Yetki liste gelince bilinir: yüklenirken yer tutucu, hatada düğme yok (ADR 0151 §1)
  const addControl = addVesselControl(state.kind === 'ready' ? state.fleet : null);
  const addButton =
    addControl === 'denied' ? (
      denied('add')
    ) : addControl === 'button' ? (
      <Button leftSection={<IconPlus size={18} />} onClick={() => setAdding(true)}>
        {t('fleet.add')}
      </Button>
    ) : null;

  const guide = (
    <Card withBorder radius="md" padding="lg" className={classes.guide} component="aside">
      <Title order={2} fz={16}>
        {t('fleet.guide.title')}
      </Title>
      <ol className={classes.steps}>
        {(['step1', 'step2', 'step3', 'step4'] as const).map((step, index) => (
          <li key={step}>
            <span className={classes.stepNo} aria-hidden>
              {index + 1}
            </span>
            <Text fz="sm">{t(`fleet.guide.${step}`)}</Text>
          </li>
        ))}
      </ol>
    </Card>
  );

  return (
    <Shell title={t('fleet.title')}>
      <div className={classes.page}>
        <PageHeader
          title={t('fleet.title')}
          description={t('fleet.subtitle')}
          actions={
            state.kind === 'loading' ? (
              <Skeleton height={36} width={128} radius="sm" aria-hidden />
            ) : (
              addButton
            )
          }
        />

        {state.kind === 'error' ? (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} role="alert">
            <Text fz="sm" mb="xs">
              {t('fleet.error')}
            </Text>
            <Button size="xs" variant="white" color="red" onClick={load}>
              {t('fleet.retry')}
            </Button>
          </Alert>
        ) : null}

        {state.kind === 'loading' ? (
          <div aria-busy="true" className={classes.loading}>
            <div className={classes.tiles}>
              {[0, 1, 2].map((tile) => (
                <Skeleton key={tile} height={74} radius="md" />
              ))}
            </div>
            <Card withBorder radius="md" padding="lg">
              {[0, 1, 2].map((row) => (
                <Skeleton key={row} height={36} radius="sm" mb={row < 2 ? 'sm' : 0} />
              ))}
            </Card>
          </div>
        ) : null}

        {state.kind === 'ready' ? (
          <div className={classes.layout}>
            <div className={classes.main}>
              <section className={classes.tiles} aria-label={t('fleet.summary.label')}>
                <StatTile
                  icon={<IconShip size={22} />}
                  label={t('fleet.summary.total')}
                  value={summary.total}
                />
                <StatTile
                  icon={<IconCircleCheck size={22} />}
                  label={t('fleet.summary.active')}
                  value={summary.active}
                />
                <StatTile
                  icon={<IconHourglassHigh size={22} />}
                  label={t('fleet.summary.pending')}
                  value={summary.pending}
                  tone={summary.pending > 0 ? 'warning' : 'default'}
                />
              </section>

              {vessels.length === 0 ? (
                <Card withBorder radius="md" padding="xl" className={classes.empty}>
                  <ThemeIcon size={48} radius="xl" variant="light">
                    <IconAnchor size={26} />
                  </ThemeIcon>
                  <Title order={2} fz={18} mt="md">
                    {t('fleet.emptyTitle')}
                  </Title>
                  <Text c="dimmed" fz="sm" mb="lg">
                    {t('fleet.emptyText')}
                  </Text>
                  {addButton}
                </Card>
              ) : (
                <>
                  <Card withBorder radius="md" padding={0} className={classes.tableCard}>
                    <Table verticalSpacing="sm" horizontalSpacing="lg" highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>{t('fleet.name')}</Table.Th>
                          <Table.Th>{t('fleet.imo')}</Table.Th>
                          <Table.Th>{t('fleet.status')}</Table.Th>
                          <Table.Th>{t('fleet.stage.label')}</Table.Th>
                          <Table.Th className={classes.actionsCell}>
                            <span className={classes.visuallyHidden}>{t('fleet.actions')}</span>
                          </Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {vessels.map((vessel) => (
                          <Table.Tr key={vessel.id}>
                            <Table.Td>
                              <span className={classes.vessel}>
                                <ThemeIcon size={36} radius="md" variant="light">
                                  <IconShip size={20} />
                                </ThemeIcon>
                                <span className={classes.stack}>
                                  <span className={classes.vesselName}>{vessel.name}</span>
                                  <Text component="span" fz="xs" c="dimmed">
                                    {t('fleet.timeZone', { zone: vessel.timeZone })}
                                  </Text>
                                </span>
                              </span>
                            </Table.Td>
                            <Table.Td className={classes.mono}>{vessel.imo ?? '—'}</Table.Td>
                            <Table.Td>{status(vessel)}</Table.Td>
                            <Table.Td>{stage(vessel)}</Table.Td>
                            <Table.Td className={classes.actionsCell}>{actions(vessel)}</Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Card>
                  {/* Dar ekranda tablo kart listesine döner (docs/ekranlar/README.md "Responsive kurallar") */}
                  <ul className={classes.cards}>
                    {vessels.map((vessel) => (
                      <li key={vessel.id}>
                        <Card withBorder radius="md" padding="md">
                          <div className={classes.cardHead}>
                            <span className={classes.vessel}>
                              <ThemeIcon size={36} radius="md" variant="light">
                                <IconShip size={20} />
                              </ThemeIcon>
                              <span className={classes.stack}>
                                <span className={classes.vesselName}>{vessel.name}</span>
                                <Text component="span" fz="xs" c="dimmed">
                                  {t('fleet.imo')} {vessel.imo ?? '—'} · {vessel.timeZone}
                                </Text>
                              </span>
                            </span>
                            <span className={classes.stack}>
                              {status(vessel)}
                              {stage(vessel)}
                            </span>
                          </div>
                          <div className={classes.cardActions}>{cardActions(vessel)}</div>
                        </Card>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            {guide}
          </div>
        ) : null}
      </div>

      <AddVesselModal
        opened={adding}
        onClose={() => setAdding(false)}
        onCreated={() => {
          setAdding(false);
          load();
        }}
      />
      <SetupCodeModal vessel={coding} onClose={() => setCoding(null)} />
      <GoLiveModal
        vessel={goingLive}
        onClose={() => setGoingLive(null)}
        onDone={() => {
          setGoingLive(null);
          load();
        }}
      />
      <VesselParticularsDrawer
        opened={particulars !== null}
        vesselId={particulars}
        load={officeApi.particulars}
        save={(vesselId, { rowVersion, ...input }) =>
          officeApi.saveParticulars(vesselId, rowVersion, input)
        }
        year={new Date().getUTCFullYear()}
        // Filo kapsamlı settings.edit yoksa salt okunur, nedeni yazılı (ADR 0151 Ek)
        readOnlyReason={
          particularsReadOnly(state.kind === 'ready' ? state.fleet : null)
            ? deniedText('particulars')
            : null
        }
        onClose={() => setParticulars(null)}
        onSaved={() => setParticulars(null)}
      />
    </Shell>
  );
}
